import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { Butterfly } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hello — I'm Meta, your guide to Metamorphosis. Ask me about our services, programs, clients, or how to book a session." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const ev of events) {
          const lines = ev.split("\n").filter(Boolean);
          let eventName = "message";
          let data = "";
          for (const l of lines) {
            if (l.startsWith("event:")) eventName = l.slice(6).trim();
            else if (l.startsWith("data:")) data = l.slice(5).trim();
          }
          if (!data) continue;
          try {
            const parsed = JSON.parse(data);
            if (eventName === "session" && parsed.session_id) setSessionId(parsed.session_id);
            else if (parsed.delta) {
              setMsgs((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: (copy[copy.length - 1].content || "") + parsed.delta,
                };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I couldn't reach the server. Please try the inquiry form or email info@mitllp.com." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 200 }}
        onClick={() => setOpen(true)}
        data-testid="chatbot-toggle"
        className="fixed bottom-6 right-6 z-40 h-16 w-16 rounded-full flex items-center justify-center shadow-2xl border border-[#1E1B4B]/40"
        style={{ background: "#FFFFFF", boxShadow: "0 20px 40px -12px rgba(30,27,75,0.35)" }}
        aria-label="Open chat"
      >
        <Butterfly size={40} />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.35 }}
            className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[420px] h-[560px] max-h-[80vh] rounded-lg overflow-hidden flex flex-col border border-[#1E1B4B]/30"
            style={{ background: "rgba(251,247,234,0.98)", backdropFilter: "blur(16px)" }}
            data-testid="chatbot-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#0A0A14]/12">
              <div className="flex items-center gap-3">
                <Butterfly size={32} />
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-[#A4801C]">META · TRANSFORMATION ADVISOR</div>
                  <div className="font-display text-lg text-[#0A0A14] mt-1">How can we help?</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#0A0A14]/65 hover:text-[#0A0A14] p-2" data-testid="chatbot-close"><X size={18} /></button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chatbot-messages">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`${m.role === "user" ? "chat-bubble-user" : "chat-bubble-bot"} px-4 py-3 rounded-lg max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap`}>
                    {m.content || <span className="opacity-60">…</span>}
                  </div>
                </div>
              ))}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="px-4 py-3 border-t border-[#0A0A14]/12 flex gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about a service, program or booking…"
                className="input-editorial text-sm"
                style={{ borderBottomColor: "rgba(10,10,20,0.15)" }}
                data-testid="chatbot-input"
              />
              <button type="submit" disabled={sending} className="btn-solid-gold px-4" data-testid="chatbot-send">
                <Send size={14} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
